/**
 * matching-v2 §7.1 — inline speaker badge.
 *
 * Renders one of four visual states per the §7.1 table, mapped from the
 * `MatchingV2Speaker.binding` + `candidates[].confidence_band` triple:
 *
 *   • «Уверены»            — active binding, no attention flag.
 *                            Renders plain text (no chevron, no buttons).
 *   • «Косвенные улики»    — top candidate.confidence_band='medium_unique'.
 *                            Renders `[Name ▾]`; clicking the chevron opens
 *                            an inline panel with the top citation + actions.
 *   • «Двое кандидатов»    — top.confidence_band='medium_ambiguous'.
 *                            Renders `[Name 1] [Name 2]`; hotkeys 1/2 fire
 *                            `onResolve` on the first matching utterance row.
 *   • «Не нашли»           — top.confidence_band='low' (or no candidates).
 *                            Renders an inline combobox with two groups —
 *                            «Участники этой встречи» + «Весь реестр» —
 *                            and a stretched chevron arrow for the
 *                            «Перепривязать всего спикера» menu.
 *
 * The badge is *purely presentational*: every mutation is dispatched
 * through the props (`onResolve`, `onRebindAll`, ...). The parent
 * `TranscriptTab` owns the react-query mutations and the snapshot cache.
 *
 * Hotkeys 1/2 are scoped per row — handled by `keydown` on the badge
 * container so two adjacent ambiguous speakers don't collide. The
 * keydown listener is attached only when the row is the active focus
 * target (mouseover/focus) so a stray keypress while typing in the
 * search box never resolves a candidate by accident.
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  EmployeeSummary,
  MatchingV2Candidate,
  MatchingV2Citation,
  MatchingV2Speaker,
  MentionType,
  UUID,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface InlineSpeakerBadgeProps {
  speaker: MatchingV2Speaker;
  /** Employees in the meeting's participants list (used by the «Не нашли» combobox). */
  participants: EmployeeSummary[];
  /** Full registry (used by the «Не нашли» combobox second group). */
  registry: EmployeeSummary[];
  /** Resolve one speaker → employee (POST /matching/resolve). */
  onResolve: (employeeId: UUID) => void;
  /** Cascade-replace across ALL utterances (POST /matching/rebind-speaker, scope='all'). */
  onRebindAll: (employeeId: UUID) => void;
}

export function InlineSpeakerBadge({
  speaker,
  participants,
  registry,
  onResolve,
  onRebindAll,
}: InlineSpeakerBadgeProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [rebindOpen, setRebindOpen] = useState(false);

  const top = speaker.candidates[0];
  const second = speaker.candidates[1];
  const band = top?.confidence_band ?? "low";

  // Hotkeys 1/2 for medium_ambiguous — only when the row container has
  // pointer / focus inside it. Otherwise an operator typing in the search
  // box would silently confirm bindings.
  useEffect(() => {
    if (band !== "medium_ambiguous") return;
    if (top === undefined) return;
    const el = containerRef.current;
    if (el === null) return;
    let hovered = false;
    const onEnter = () => {
      hovered = true;
    };
    const onLeave = () => {
      hovered = false;
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (!hovered) return;
      if (e.key === "1") {
        e.preventDefault();
        onResolve(top.suggested_employee_id);
      } else if (e.key === "2" && second !== undefined) {
        e.preventDefault();
        onResolve(second.suggested_employee_id);
      }
    };
    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mouseleave", onLeave);
    el.addEventListener("focusin", onEnter);
    el.addEventListener("focusout", onLeave);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mouseleave", onLeave);
      el.removeEventListener("focusin", onEnter);
      el.removeEventListener("focusout", onLeave);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [band, top, second, onResolve]);

  const dashed =
    speaker.binding?.source === "rebuild_preserved" ||
    speaker.requires_attention;

  // ── State 1: «Уверены» ────────────────────────────────────────────────
  if (
    speaker.binding !== null &&
    (speaker.binding.status === "active" ||
      speaker.binding.status === "pending_confirm") &&
    !speaker.requires_attention
  ) {
    return (
      <span
        ref={containerRef}
        className={cn(
          "inline-flex items-center gap-1 text-sm font-medium",
          dashed && "border-b border-dashed border-amber-500",
        )}
        data-testid="inline-speaker-badge"
        data-band="high"
        data-source={speaker.binding.source}
      >
        <span data-testid="badge-name">
          {speaker.binding.employee_full_name}
        </span>
        {speaker.binding.status === "pending_confirm" ? (
          <span
            className="ml-1 inline-flex items-center rounded-full bg-amber-50 px-1.5 py-[1px] text-[10px] font-medium text-amber-700"
            data-testid="badge-pending-confirm"
            aria-label={t("meetings.card.matchingV2.badge.pendingConfirmAria")}
          >
            {t("meetings.card.matchingV2.badge.pendingConfirm")}
          </span>
        ) : null}
        <RebindMenu
          speaker={speaker}
          participants={participants}
          registry={registry}
          open={rebindOpen}
          onOpenChange={setRebindOpen}
          onRebindAll={onRebindAll}
          onResolve={onResolve}
        />
      </span>
    );
  }

  // ── State 2: «Косвенные улики» (medium_unique) ────────────────────────
  if (band === "medium_unique" && top !== undefined) {
    return (
      <span
        ref={containerRef}
        className={cn(
          "inline-flex flex-col gap-1",
          dashed && "border-b border-dashed border-amber-500",
        )}
        data-testid="inline-speaker-badge"
        data-band="medium_unique"
      >
        <span className="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={() => setPanelOpen((v) => !v)}
            className="rounded border border-input bg-amber-50 px-1.5 py-[1px] text-sm font-medium text-amber-900 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            aria-expanded={panelOpen}
            data-testid="badge-medium-unique-trigger"
          >
            <span data-testid="badge-name">{top.employee_full_name}</span>
            <span aria-hidden className="ml-1 text-xs">
              {panelOpen ? "▴" : "▾"}
            </span>
          </button>
          <RebindMenu
            speaker={speaker}
            participants={participants}
            registry={registry}
            open={rebindOpen}
            onOpenChange={setRebindOpen}
            onRebindAll={onRebindAll}
            onResolve={onResolve}
          />
        </span>
        {panelOpen ? (
          <EvidencePanel
            candidate={top}
            onConfirm={() => onResolve(top.suggested_employee_id)}
            onReject={() => setPanelOpen(false)}
          />
        ) : null}
      </span>
    );
  }

  // ── State 3: «Двое кандидатов» (medium_ambiguous) ─────────────────────
  if (
    band === "medium_ambiguous" &&
    top !== undefined &&
    second !== undefined
  ) {
    return (
      <span
        ref={containerRef}
        className={cn(
          "inline-flex flex-col gap-1",
          dashed && "border-b border-dashed border-amber-500",
        )}
        data-testid="inline-speaker-badge"
        data-band="medium_ambiguous"
        tabIndex={0}
      >
        <span className="inline-flex flex-wrap items-center gap-1">
          <button
            type="button"
            onClick={() => onResolve(top.suggested_employee_id)}
            className="rounded border border-input bg-background px-1.5 py-[1px] text-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            data-testid="badge-ambiguous-1"
            aria-label={t("meetings.card.matchingV2.badge.ambiguousHotkey", {
              name: top.employee_full_name,
              n: 1,
            })}
          >
            {top.employee_full_name}
            <kbd className="ml-1 rounded bg-muted px-1 font-mono text-[10px]">
              1
            </kbd>
          </button>
          <button
            type="button"
            onClick={() => onResolve(second.suggested_employee_id)}
            className="rounded border border-input bg-background px-1.5 py-[1px] text-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            data-testid="badge-ambiguous-2"
            aria-label={t("meetings.card.matchingV2.badge.ambiguousHotkey", {
              name: second.employee_full_name,
              n: 2,
            })}
          >
            {second.employee_full_name}
            <kbd className="ml-1 rounded bg-muted px-1 font-mono text-[10px]">
              2
            </kbd>
          </button>
          <button
            type="button"
            onClick={() => setPanelOpen((v) => !v)}
            className="text-xs text-primary hover:underline"
            aria-expanded={panelOpen}
            data-testid="badge-ambiguous-why"
          >
            {panelOpen
              ? t("meetings.card.matchingV2.badge.hideWhy")
              : t("meetings.card.matchingV2.badge.why")}
          </button>
          <RebindMenu
            speaker={speaker}
            participants={participants}
            registry={registry}
            open={rebindOpen}
            onOpenChange={setRebindOpen}
            onRebindAll={onRebindAll}
            onResolve={onResolve}
          />
        </span>
        {panelOpen ? (
          <div
            className="flex flex-col gap-2"
            data-testid="badge-ambiguous-panel"
          >
            <EvidencePanel
              candidate={top}
              onConfirm={() => onResolve(top.suggested_employee_id)}
              onReject={() => setPanelOpen(false)}
              compact
            />
            <EvidencePanel
              candidate={second}
              onConfirm={() => onResolve(second.suggested_employee_id)}
              onReject={() => setPanelOpen(false)}
              compact
            />
          </div>
        ) : null}
      </span>
    );
  }

  // ── State 4: «Не нашли» (low) ─────────────────────────────────────────
  return (
    <span
      ref={containerRef}
      className={cn(
        "inline-flex items-center gap-1",
        dashed && "border-b border-dashed border-amber-500",
      )}
      data-testid="inline-speaker-badge"
      data-band="low"
    >
      <EmployeeComboBox
        participants={participants}
        registry={registry}
        onPick={onResolve}
        speakerLabel={speaker.label}
      />
      <RebindMenu
        speaker={speaker}
        participants={participants}
        registry={registry}
        open={rebindOpen}
        onOpenChange={setRebindOpen}
        onRebindAll={onRebindAll}
        onResolve={onResolve}
      />
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Evidence panel — «Почему этот?» disclosure for medium states
// ─────────────────────────────────────────────────────────────────────────

interface EvidencePanelProps {
  candidate: MatchingV2Candidate;
  onConfirm: () => void;
  onReject: () => void;
  compact?: boolean;
}

function EvidencePanel({
  candidate,
  onConfirm,
  onReject,
  compact = false,
}: EvidencePanelProps) {
  const { t } = useTranslation();
  const topCitation = candidate.evidence.citations[0];
  return (
    <div
      className={cn(
        "rounded border border-input bg-muted/40 px-2 py-1.5 text-xs",
        compact ? "max-w-md" : "",
      )}
      data-testid="evidence-panel"
      data-candidate-id={candidate.id}
    >
      <div className="font-medium text-foreground">
        {candidate.employee_full_name}
      </div>
      {topCitation !== undefined ? (
        <CitationLine
          citation={topCitation}
          employeeName={candidate.employee_full_name}
        />
      ) : (
        <p className="text-muted-foreground">
          {t("meetings.card.matchingV2.evidence.empty")}
        </p>
      )}
      {candidate.llm_decision !== null ? (
        <div
          className="mt-2 rounded border border-blue-200 bg-blue-50 px-2 py-1 text-blue-900"
          data-testid="evidence-llm"
        >
          <div className="text-[10px] font-semibold uppercase tracking-wider">
            {t("meetings.card.matchingV2.evidence.llmHint")}
            {candidate.llm_decision.cached ? (
              <span className="ml-1 rounded bg-blue-100 px-1 text-[9px]">
                {t("meetings.card.matchingV2.evidence.llmCached")}
              </span>
            ) : null}
          </div>
          <p className="mt-0.5">{candidate.llm_decision.reasoning}</p>
        </div>
      ) : null}
      <div className="mt-2 flex gap-1">
        <Button
          size="sm"
          variant="default"
          onClick={onConfirm}
          data-testid="evidence-confirm"
        >
          {t("meetings.card.matchingV2.evidence.confirm")}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onReject}
          data-testid="evidence-reject"
        >
          {t("meetings.card.matchingV2.evidence.reject")}
        </Button>
      </div>
    </div>
  );
}

interface CitationLineProps {
  citation: MatchingV2Citation;
  employeeName: string;
}

function CitationLine({ citation, employeeName }: CitationLineProps) {
  const { t } = useTranslation();
  const timestamp = useMemo(
    () => formatMs(citation.start_ms),
    [citation.start_ms],
  );
  const mentionTypeLabel = t(
    `meetings.card.matchingV2.evidence.mentionType.${citation.mention_type}`,
  );
  return (
    <p className="mt-1 text-muted-foreground" data-testid="citation-line">
      <span className="mr-1 rounded bg-muted px-1 text-[10px] font-mono">
        [{timestamp}]
      </span>
      <span className="mr-1 rounded bg-amber-100 px-1 text-[10px] text-amber-900">
        {mentionTypeLabel}
      </span>
      <span className="italic">
        «<HighlightedQuote text={citation.quote} name={employeeName} />»
      </span>
    </p>
  );
}

/**
 * Case-insensitive `name` lookup inside the citation quote, wrapped in
 * `<strong>`. Mirrors the algorithm in `entityHighlight.splitTextByEntities`
 * but operates on a single name + single text — no need for the full
 * multi-entity pass.
 */
function HighlightedQuote({ text, name }: { text: string; name: string }) {
  if (name.length === 0) return <>{text}</>;
  const lower = text.toLowerCase();
  const needle = name.toLowerCase();
  const idx = lower.indexOf(needle);
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <strong data-testid="citation-name-highlight">
        {text.slice(idx, idx + needle.length)}
      </strong>
      {text.slice(idx + needle.length)}
    </>
  );
}

function formatMs(ms: number): string {
  const safe = Number.isFinite(ms) && ms > 0 ? Math.floor(ms / 1000) : 0;
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// ─────────────────────────────────────────────────────────────────────────
// «Не нашли» combobox — two groups: participants of this meeting + registry
// ─────────────────────────────────────────────────────────────────────────

interface EmployeeComboBoxProps {
  participants: EmployeeSummary[];
  registry: EmployeeSummary[];
  onPick: (employeeId: UUID) => void;
  speakerLabel: string;
}

function EmployeeComboBox({
  participants,
  registry,
  onPick,
  speakerLabel,
}: EmployeeComboBoxProps) {
  const { t } = useTranslation();
  const id = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Exclude participants from the registry list — no duplicates across groups.
  const participantIds = useMemo(
    () => new Set(participants.map((p) => p.id)),
    [participants],
  );
  const registryOnly = useMemo(
    () => registry.filter((e) => !participantIds.has(e.id)),
    [registry, participantIds],
  );

  const filteredParticipants = useMemo(
    () => filterEmployees(participants, query),
    [participants, query],
  );
  const filteredRegistry = useMemo(
    () => filterEmployees(registryOnly, query),
    [registryOnly, query],
  );

  return (
    <span className="relative inline-flex" data-testid="employee-combobox">
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={t("meetings.card.matchingV2.combobox.placeholder", {
          label: speakerLabel,
        })}
        className="w-48 rounded border border-input bg-background px-2 py-[1px] text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        data-testid="employee-combobox-input"
      />
      {open ? (
        <div
          className="absolute left-0 top-full z-30 mt-1 max-h-64 w-72 overflow-auto rounded border border-input bg-background p-1 text-sm shadow-md"
          data-testid="employee-combobox-list"
        >
          <ComboboxGroup
            title={t("meetings.card.matchingV2.combobox.groupParticipants", {
              count: filteredParticipants.length,
            })}
            employees={filteredParticipants}
            onPick={(e) => {
              onPick(e.id);
              setOpen(false);
              setQuery("");
            }}
            testid="combobox-group-participants"
          />
          <ComboboxGroup
            title={t("meetings.card.matchingV2.combobox.groupRegistry")}
            employees={filteredRegistry.slice(0, 20)}
            onPick={(e) => {
              onPick(e.id);
              setOpen(false);
              setQuery("");
            }}
            testid="combobox-group-registry"
          />
          {filteredParticipants.length === 0 &&
          filteredRegistry.length === 0 ? (
            <p className="px-2 py-1 text-xs text-muted-foreground">
              {t("meetings.card.matchingV2.combobox.empty")}
            </p>
          ) : null}
        </div>
      ) : null}
    </span>
  );
}

function filterEmployees(
  list: EmployeeSummary[],
  query: string,
): EmployeeSummary[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return list;
  return list.filter((e) => e.full_name.toLowerCase().includes(q));
}

interface ComboboxGroupProps {
  title: string;
  employees: EmployeeSummary[];
  onPick: (e: EmployeeSummary) => void;
  testid: string;
}

function ComboboxGroup({
  title,
  employees,
  onPick,
  testid,
}: ComboboxGroupProps) {
  if (employees.length === 0) return null;
  return (
    <div className="py-1" data-testid={testid}>
      <p className="px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </p>
      <ul>
        {employees.map((e) => (
          <li key={e.id}>
            <button
              type="button"
              onMouseDown={(ev) => {
                ev.preventDefault();
                onPick(e);
              }}
              className="w-full rounded px-2 py-1 text-left hover:bg-muted"
              data-testid={`combobox-employee-${e.id}`}
            >
              {e.full_name}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// «Перепривязать всего спикера» menu — chevron arrow → search → confirm dialog
// ─────────────────────────────────────────────────────────────────────────

interface RebindMenuProps {
  speaker: MatchingV2Speaker;
  participants: EmployeeSummary[];
  registry: EmployeeSummary[];
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Cascade across all utterances. */
  onRebindAll: (employeeId: UUID) => void;
  /** Bind only this segment (alias for `onResolve`, "Только эта"). */
  onResolve: (employeeId: UUID) => void;
}

function RebindMenu({
  speaker,
  participants,
  registry,
  open,
  onOpenChange,
  onRebindAll,
  onResolve,
}: RebindMenuProps) {
  const { t } = useTranslation();
  const [picked, setPicked] = useState<EmployeeSummary | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        className="rounded px-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("meetings.card.matchingV2.rebind.menuAria")}
        data-testid="rebind-menu-trigger"
      >
        ⌄
      </button>
      {open ? (
        <div
          className="absolute right-0 top-full z-30 mt-1 w-72 rounded border border-input bg-background p-1 shadow-md"
          data-testid="rebind-menu-panel"
        >
          <EmployeeComboBox
            participants={participants}
            registry={registry}
            onPick={(employeeId) => {
              const found =
                participants.find((p) => p.id === employeeId) ??
                registry.find((p) => p.id === employeeId) ??
                null;
              if (found === null) return;
              setPicked(found);
              setConfirmOpen(true);
              onOpenChange(false);
            }}
            speakerLabel={speaker.label}
          />
        </div>
      ) : null}
      {confirmOpen && picked !== null ? (
        <RebindConfirm
          picked={picked}
          // We can't know the exact utterance count from inside the badge —
          // the parent passes `speaker.total_words` which the dialog scales
          // into a rough «around N replies» for the operator. Using
          // `speaker.candidates[].evidence.citations.length` would
          // under-count silent replies; words is the only proxy we have
          // without a separate utterances index in the v2 shape.
          totalWords={speaker.total_words}
          onAll={() => {
            onRebindAll(picked.id);
            setConfirmOpen(false);
            setPicked(null);
          }}
          onSingle={() => {
            onResolve(picked.id);
            setConfirmOpen(false);
            setPicked(null);
          }}
          onCancel={() => {
            setConfirmOpen(false);
            setPicked(null);
          }}
        />
      ) : null}
    </span>
  );
}

interface RebindConfirmProps {
  picked: EmployeeSummary;
  totalWords: number;
  onAll: () => void;
  onSingle: () => void;
  onCancel: () => void;
}

function RebindConfirm({
  picked,
  totalWords,
  onAll,
  onSingle,
  onCancel,
}: RebindConfirmProps) {
  const { t } = useTranslation();
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("meetings.card.matchingV2.rebind.confirmTitle")}
      className="fixed left-1/2 top-1/2 z-50 w-[480px] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-input bg-background p-4 shadow-xl"
      data-testid="rebind-confirm"
    >
      <h3 className="text-base font-semibold">
        {t("meetings.card.matchingV2.rebind.confirmTitle")}
      </h3>
      <p className="mt-2 text-sm text-muted-foreground">
        {t("meetings.card.matchingV2.rebind.confirmBody", {
          name: picked.full_name,
          words: totalWords,
        })}
      </p>
      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button
          variant="default"
          size="sm"
          onClick={onAll}
          data-testid="rebind-confirm-all"
        >
          {t("meetings.card.matchingV2.rebind.confirmAll")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onSingle}
          data-testid="rebind-confirm-single"
        >
          {t("meetings.card.matchingV2.rebind.confirmSingle")}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          data-testid="rebind-confirm-cancel"
        >
          {t("meetings.card.matchingV2.rebind.confirmCancel")}
        </Button>
      </div>
    </div>
  );
}

// Type-checker pacifier: keep MentionType in the import graph (used by the
// citation map indirectly through the i18n key).
export type _MentionTypeKept = MentionType;
